import { useRouteLoaderData } from "react-router-dom";
import {
  hasUnlimitedSharedProfilesVisibility,
  isFreeOrganizationAccount,
  Organization,
} from "../../services/organization/organizationModel";
import { User } from "../../services/user/userModel";
import ProfileCard from "../candidate/profile/ProfileCardComponent";

import {
  compareBoolean,
  compareDate,
  multiSorts,
  useSearch,
} from "../../react-helpers/array";
import { useEffect, useMemo, useRef, useState } from "react";
import { organizationService } from "../../services/organization/organizationService";
import { useConfirmationWithIntl } from "../../components/ConfirmationDialog";
import { useTranslation } from "react-i18next";
import useReload from "../../hooks/useReload";
import InviteCandidatesDialog from "./InviteCandidatesDialog";
import SubscribeDialog from "./pro/SubscribeDialog";
import Pagination from "../../components/Pagination";
import proTutorialVideoFr from "../../assets/video/2mm_tutorial_pro_new.fr.mp4";
import proTutorialVideoEn from "../../assets/video/2mm_tutorial_pro_new.en.mp4";

// Only a French cut exists for now: every other language gets the English one
const proTutorialVideosByLang: Record<string, string> = {
  fr: proTutorialVideoFr,
};

enum Tab {
  Shared,
  Archived,
  Favorited,
}

const PAGE_SIZE = 12;
// Remember the current page across navigation so returning from a candidate
// profile lands back on the same page (kept for the browser tab session).
const PAGE_STORAGE_KEY = "organization-candidates-page";
// Profil qu'on vient de lire (posé par SharedPage) : au retour, on rouvre la page de sa vignette et on la montre,
// en PRO comme en PRO + (Nicolas, 01/11).
export const LAST_VIEWED_PROFILE_KEY = "organization-candidates-last-viewed";

const OrganizationCandidatesProfilesPage = () => {
  const { t, i18n } = useTranslation();
  const { organization } = useRouteLoaderData("organization") as {
    organization: Organization;
  };
  const { loggedUser } = useRouteLoaderData("logged-user") as {
    loggedUser: User;
  };
  const {
    archiveSharedProfile,
    unarchiveSharedProfile,
    favoriteSharedProfile,
    unfavoriteSharedProfile,
    deleteSharedProfile,
  } = organizationService();

  const { confirm } = useConfirmationWithIntl(["organization"]);
  const reload = useReload();

  const [searchedProfiles, setSearch] = useSearch(
    organization.sharedProfiles,
    (e) => ({
      profile: {
        user: {
          lastname: e.profile.user?.lastname,
          firstname: e.profile.user?.firstname,
        },
      },
    }),
  );

  const sharedProfiles = useMemo(() => {
    return searchedProfiles
      .filter((p) => p.archivedAt === null)
      .sort(
        multiSorts(
          // put disabled profiles at the end of the list
          (a, b) =>
            compareBoolean(!a.profile.disabledDate, !b.profile.disabledDate),
          // free plan: watchable videos first, locked history after the banner
          (a, b) => compareBoolean(!a.locked, !b.locked),
          // most recently shared first
          (a, b) => compareDate(new Date(b.createdAt), new Date(a.createdAt)),
        ),
      );
  }, [searchedProfiles]);

  const archivedProfiles = useMemo(() => {
    return searchedProfiles
      .filter((p) => p.archivedAt !== null)
      .sort(
        multiSorts(
          (a, b) =>
            compareBoolean(!a.profile.disabledDate, !b.profile.disabledDate),
          (a, b) => compareDate(new Date(b.createdAt), new Date(a.createdAt)),
        ),
      );
  }, [searchedProfiles]);

  const favoritedProfiles = useMemo(() => {
    return searchedProfiles
      .filter((p) => p.favoritedAt !== null && p.archivedAt === null)
      .sort(
        multiSorts(
          (a, b) =>
            compareBoolean(!a.profile.disabledDate, !b.profile.disabledDate),
          (a, b) => compareDate(new Date(b.createdAt), new Date(a.createdAt)),
        ),
      );
  }, [searchedProfiles]);

  const [currentTab, setCurrentTab] = useState(Tab.Shared);
  const [isInviteDialogOpen, setInviteDialogOpen] = useState(false);
  const [isSubscribeDialogOpen, setSubscribeDialogOpen] = useState(false);
  const [returnUid] = useState(() =>
    sessionStorage.getItem(LAST_VIEWED_PROFILE_KEY),
  );
  const [currentPage, setCurrentPage] = useState(() => {
    const index = sharedProfiles.findIndex((p) => p.profile.uid === returnUid);
    if (index >= 0) return Math.floor(index / PAGE_SIZE) + 1;
    const stored = Number(sessionStorage.getItem(PAGE_STORAGE_KEY));
    return Number.isInteger(stored) && stored > 0 ? stored : 1;
  });

  // Persist the page so it can be restored when coming back to this page.
  useEffect(() => {
    sessionStorage.setItem(PAGE_STORAGE_KEY, String(currentPage));
  }, [currentPage]);

  const activeList = useMemo(() => {
    switch (currentTab) {
      case Tab.Favorited:
        return favoritedProfiles;
      case Tab.Archived:
        return archivedProfiles;
      default:
        return sharedProfiles;
    }
  }, [currentTab, sharedProfiles, archivedProfiles, favoritedProfiles]);

  const pageCount = Math.max(1, Math.ceil(activeList.length / PAGE_SIZE));

  const visibleProfiles = useMemo(
    () =>
      activeList.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE),
    [activeList, currentPage],
  );

  // Free plan: the page splits around the unlock banner
  const visibleUnlockedProfiles = useMemo(
    () => visibleProfiles.filter((p) => !p.locked),
    [visibleProfiles],
  );
  const visibleLockedProfiles = useMemo(
    () => visibleProfiles.filter((p) => p.locked),
    [visibleProfiles],
  );

  useEffect(() => {
    if (!returnUid) return;
    sessionStorage.removeItem(LAST_VIEWED_PROFILE_KEY);
    // Après coup : la coquille remonte en haut de page à chaque navigation, dans un effet qui passe après celui-ci.
    const timer = setTimeout(() => {
      const card = document.querySelector<HTMLElement>(
        `a[href="/profile-share/${CSS.escape(returnUid)}"]`,
      );
      card?.classList.add("--last-viewed");
      card?.scrollIntoView({ block: "center" });
    }, 0);
    return () => clearTimeout(timer);
  }, [returnUid]);

  // Reset to the first page whenever the tab changes (but not on the initial
  // render, so a restored page survives a return navigation).
  const previousTab = useRef(currentTab);
  useEffect(() => {
    if (previousTab.current !== currentTab) {
      previousTab.current = currentTab;
      setCurrentPage(1);
    }
  }, [currentTab]);

  // Keep the current page within bounds when the list shrinks (search,
  // archive/favorite/delete actions, …).
  useEffect(() => {
    setCurrentPage((page) => Math.min(page, pageCount));
  }, [pageCount]);

  const onSearch = (value: string) => {
    setSearch(value);
    setCurrentPage(1);
  };

  const isFreeAccount = useMemo(
    () => isFreeOrganizationAccount(organization, loggedUser),
    [organization, loggedUser],
  );

  // Invited member of an organization: the backend already unlocks the whole
  // history, so the freemium "limited to the 3 last profiles" banner would be
  // wrong even though the account has no paid subscription.
  const hasUnlimitedVisibility = useMemo(
    () => hasUnlimitedSharedProfilesVisibility(loggedUser),
    [loggedUser],
  );

  // Shown to every free account, whatever the number of (locked) videos —
  // except invited members: their history is already fully unlocked, so the
  // "unlock your history" pitch would be meaningless.
  const proTutorialVideoSrc =
    proTutorialVideosByLang[i18n.language.split("-")[0]] ?? proTutorialVideoEn;

  const unlockHistoryBanner = isFreeAccount && !hasUnlimitedVisibility && (
    <section className="pro-unlock">
      <div>
        <h2 className="pro-unlock_title">
          {t("organization:candidates-profiles.UNLOCK_HISTORY_TITLE")}
        </h2>
        <ul className="pro-unlock_list">
          <li>{t("organization:candidates-profiles.UNLOCK_HISTORY_TEXT_1")}</li>
          <li>{t("organization:candidates-profiles.UNLOCK_HISTORY_TEXT_2")}</li>
          <li>{t("organization:candidates-profiles.UNLOCK_HISTORY_TEXT_3")}</li>
        </ul>
        <div className="pro-unlock_cta">
          <a
            className="btn--white"
            href="https://www.2minutesmax.com/espace-professionnel/"
            target="_blank"
            rel="noreferrer"
          >
            {t("organization:candidates-profiles.DISCOVER_WORKSPACE_PRO")}
          </a>
          {loggedUser.adminOrga ? (
            <button
              className="btn--1"
              onClick={() => setSubscribeDialogOpen(true)}
            >
              {t("organization:candidates-profiles.TRY_PRO_CTA")}
            </button>
          ) : (
            <div>
              {t("organization:candidates-profiles.LOCKED_CONTACT_ADMIN")}
            </div>
          )}
        </div>
      </div>
      <video
        className="pro-unlock_video"
        src={proTutorialVideoSrc}
        controls
        playsInline
        preload="metadata"
        onContextMenu={(e) => e.preventDefault()}
      />
    </section>
  );

  const renderSharedProfileCard = (
    profile: Organization["sharedProfiles"][number],
  ) => (
    <ProfileCard
      key={profile.profileId}
      profile={profile.profile}
      locked={!!profile.locked}
      canUpgrade={loggedUser.adminOrga}
      onUpgrade={() => setSubscribeDialogOpen(true)}
      isArchived={!!profile.archivedAt}
      // Archiving and favorites are PRO features: no action icons on free plan
      onArchive={
        isFreeAccount
          ? undefined
          : () => {
              confirm(
                "organization:shared-profile.archive.CONFIRM",
                () =>
                  archiveSharedProfile(
                    profile.profile.uid,
                    organization.id,
                  ).then(() => {
                    reload();
                  }),
                true,
              );
            }
      }
      isFavorited={!!profile.favoritedAt}
      onFavorite={
        isFreeAccount
          ? undefined
          : async () => {
              await favoriteSharedProfile(profile.profile.uid, organization.id);
              reload();
            }
      }
      onUnfavorite={
        isFreeAccount
          ? undefined
          : async () => {
              await unfavoriteSharedProfile(
                profile.profile.uid,
                organization.id,
              );
              reload();
            }
      }
    />
  );

  useEffect(() => {
    if (currentTab === Tab.Archived && archivedProfiles.length === 0) {
      setCurrentTab(Tab.Shared);
    }
  }, [archivedProfiles.length, currentTab]);

  useEffect(() => {
    if (currentTab === Tab.Favorited && favoritedProfiles.length === 0) {
      setCurrentTab(Tab.Shared);
    }
  }, [favoritedProfiles.length, currentTab]);

  return (
    <div className="layout_content --bg--organization-dashboard">
      <div className="ui-page pro-screen">
        <div className="ui-ph">
          <div className="ui-ph_t">
            <h1>
              {t("organization:candidates-profiles.MY_CANDIDATE_PROFILES")}
            </h1>
          </div>
        </div>

        {isFreeAccount && !hasUnlimitedVisibility && (
          <div className="ui-banner">
            <span className="pro-ico --info" aria-hidden="true" />
            <div className="ui-banner_grow">
              <b>{t("organization:candidates-profiles.FREEMIUM_TITLE")}</b>
              {t("organization:candidates-profiles.FREEMIUM_TEXT")}
            </div>
          </div>
        )}

        <div className="ui-banner --ok pro-invite">
          <span className="pro-ico --send" aria-hidden="true" />
          <div className="ui-banner_grow">
            <b>{t("organization:candidates-profiles.INVITE_CANDIDATES")}</b>
            {t("organization:candidates-profiles.INVITE_MAIL_MESSAGE")}
          </div>
          <button className="btn" onClick={() => setInviteDialogOpen(true)}>
            {t("organization:candidates-profiles.INVITE_BUTTON")}
          </button>
        </div>

        {(archivedProfiles.length > 0 || favoritedProfiles.length > 0) && (
          <div className="ui-tabs" role="tablist">
            <button
              role="tab"
              aria-selected={currentTab === Tab.Shared}
              onClick={() => setCurrentTab(Tab.Shared)}
            >
              {t("organization:candidates-profiles.SHARED_PROFILES")}
              <span className="ui-tabs_n">{sharedProfiles.length}</span>
            </button>
            {favoritedProfiles.length > 0 && (
              <button
                role="tab"
                aria-selected={currentTab === Tab.Favorited}
                onClick={() => setCurrentTab(Tab.Favorited)}
              >
                {t("organization:candidates-profiles.FAVORITED_PROFILES")}
                <span className="ui-tabs_n">{favoritedProfiles.length}</span>
              </button>
            )}
            {archivedProfiles.length > 0 && (
              <button
                role="tab"
                aria-selected={currentTab === Tab.Archived}
                onClick={() => setCurrentTab(Tab.Archived)}
              >
                {t("organization:candidates-profiles.ARCHIVED_PROFILES")}
                <span className="ui-tabs_n">{archivedProfiles.length}</span>
              </button>
            )}
          </div>
        )}

        {organization.sharedProfiles.length === 0 && (
          <>
            <div className="ui-banner">
              <span className="pro-ico --info" aria-hidden="true" />
              <div className="ui-banner_grow">
                <b>{t("organization:candidates-profiles.NO_PROFILES_YET")}</b>
                {t("organization:candidates-profiles.NO_PROFILES_MESSAGE")}
              </div>
            </div>
            {unlockHistoryBanner}
          </>
        )}

        {organization.sharedProfiles.length > 0 && (
          <>
            <div className="pro-search">
              <span className="pro-ico --search" aria-hidden="true" />
              <input
                type="search"
                className="input"
                placeholder={t(
                  "organization:candidates-profiles.SEARCH_PROFILE",
                )}
                aria-label={t(
                  "organization:candidates-profiles.SEARCH_PROFILE",
                )}
                onChange={(e) => onSearch(e.target.value)}
              />
            </div>
            {currentTab === Tab.Shared && (
              <>
                {visibleUnlockedProfiles.length > 0 && (
                  <div className="pro-grid">
                    {visibleUnlockedProfiles.map(renderSharedProfileCard)}
                  </div>
                )}
                {unlockHistoryBanner}
                {visibleLockedProfiles.length > 0 && (
                  <div className="pro-grid">
                    {visibleLockedProfiles.map(renderSharedProfileCard)}
                  </div>
                )}
              </>
            )}
            {currentTab !== Tab.Shared && (
              <div className="pro-grid">
                {currentTab === Tab.Favorited &&
                  visibleProfiles.map((profile) => (
                    <ProfileCard
                      key={profile.profileId}
                      profile={profile.profile}
                      locked={!!profile.locked}
                      canUpgrade={loggedUser.adminOrga}
                      onUpgrade={() => setSubscribeDialogOpen(true)}
                      isArchived={!!profile.archivedAt}
                      onArchive={
                        isFreeAccount
                          ? undefined
                          : () => {
                              confirm(
                                "organization:shared-profile.archive.CONFIRM",
                                () =>
                                  archiveSharedProfile(
                                    profile.profile.uid,
                                    organization.id,
                                  ).then(() => {
                                    reload();
                                  }),
                                true,
                              );
                            }
                      }
                      isFavorited={!!profile.favoritedAt}
                      onUnfavorite={
                        isFreeAccount
                          ? undefined
                          : async () => {
                              await unfavoriteSharedProfile(
                                profile.profile.uid,
                                organization.id,
                              );
                              reload();
                            }
                      }
                    />
                  ))}
                {currentTab === Tab.Archived &&
                  visibleProfiles.map((profile) => (
                    <ProfileCard
                      key={profile.profileId}
                      profile={profile.profile}
                      locked={!!profile.locked}
                      canUpgrade={loggedUser.adminOrga}
                      onUpgrade={() => setSubscribeDialogOpen(true)}
                      isArchived={!!profile.archivedAt}
                      onUnarchive={
                        isFreeAccount
                          ? undefined
                          : () => {
                              confirm(
                                "organization:shared-profile.unarchive.CONFIRM",
                                () =>
                                  unarchiveSharedProfile(
                                    profile.profile.uid,
                                    organization.id,
                                  ).then(() => {
                                    reload();
                                  }),
                                true,
                              );
                            }
                      }
                      onDelete={() => {
                        confirm(
                          "organization:shared-profile.delete.CONFIRM",
                          () =>
                            deleteSharedProfile(
                              profile.profile.uid,
                              organization.id,
                            ).then(() => {
                              reload();
                            }),
                          true,
                        );
                      }}
                    />
                  ))}
              </div>
            )}
            <Pagination
              currentPage={currentPage}
              pageCount={pageCount}
              onPageChange={setCurrentPage}
            />
          </>
        )}
      </div>
      {isInviteDialogOpen && (
        <InviteCandidatesDialog
          organization={organization}
          onClose={() => setInviteDialogOpen(false)}
        />
      )}
      {isSubscribeDialogOpen && (
        <SubscribeDialog
          organization={organization}
          onClose={() => setSubscribeDialogOpen(false)}
        />
      )}
    </div>
  );
};

export default OrganizationCandidatesProfilesPage;
